import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, ShieldCheck, ArrowRight, Lock, CheckCircle2, Smartphone, Landmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { total, clearCart } = useCartStore();
  const [paymentMethod, setPaymentMethod] = React.useState('upi');
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      clearCart();
      navigate('/success');
    }, 2500);
  };

  return (
    <div className="min-h-screen bg-midnight py-24 noise-overlay">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-20">
          <h1 className="text-5xl font-serif font-bold mb-4">Checkout</h1>
          <p className="text-gray-500 font-light">Complete your order to begin your celestial journey.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: Forms */}
          <div className="lg:col-span-7 space-y-10">
            {/* Shipping Info */}
            <div className="glass-card p-10 md:p-12">
              <h2 className="text-2xl font-serif font-bold mb-10 flex items-center">
                <span className="w-10 h-10 bg-gold/10 text-gold rounded-full flex items-center justify-center text-sm font-bold mr-4 border border-gold/20">1</span>
                Shipping Details
              </h2>
              <form className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-600">First Name</label>
                  <input type="text" className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white" placeholder="John" required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Last Name</label>
                  <input type="text" className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white" placeholder="Doe" required />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Email Address</label>
                  <input type="email" className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white" placeholder="john@example.com" required />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Phone Number</label>
                  <input type="text" className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white" placeholder="+91 98765 43210" required />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Full Address</label>
                  <textarea rows={3} className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white" placeholder="Street, Apartment, etc." required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-600">City</label>
                  <input type="text" className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white" placeholder="Mumbai" required />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-600">Pincode</label>
                  <input type="text" className="w-full px-6 py-4 bg-white/5 border border-white/10 rounded-2xl focus:border-gold outline-none text-sm text-white" placeholder="400001" required />
                </div>
              </form>
            </div>

            {/* Payment Method */}
            <div className="glass-card p-10 md:p-12">
              <h2 className="text-2xl font-serif font-bold mb-10 flex items-center">
                <span className="w-10 h-10 bg-gold/10 text-gold rounded-full flex items-center justify-center text-sm font-bold mr-4 border border-gold/20">2</span>
                Payment Method
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { id: 'upi', name: 'UPI', icon: <Smartphone className="w-5 h-5" />, desc: 'GPay, PhonePe' },
                  { id: 'card', name: 'Card', icon: <CreditCard className="w-5 h-5" />, desc: 'Credit / Debit' },
                  { id: 'netbanking', name: 'Bank', icon: <Landmark className="w-5 h-5" />, desc: 'Net Banking' }
                ].map((method) => (
                  <label 
                    key={method.id}
                    className={`flex flex-col p-6 rounded-2xl border cursor-pointer transition-all duration-300 ${
                      paymentMethod === method.id 
                        ? 'bg-gold/10 border-gold shadow-lg shadow-gold/5' 
                        : 'bg-white/5 border-white/10 text-gray-500 hover:border-gold/30'
                    }`}
                  >
                    <input 
                      type="radio" 
                      name="payment" 
                      className="hidden" 
                      checked={paymentMethod === method.id}
                      onChange={() => setPaymentMethod(method.id)}
                    />
                    <div className={`mb-4 ${paymentMethod === method.id ? 'text-gold' : 'text-gray-600'}`}>
                      {method.icon}
                    </div>
                    <span className={`text-sm font-bold uppercase tracking-widest ${paymentMethod === method.id ? 'text-white' : 'text-gray-500'}`}>
                      {method.name}
                    </span>
                    <span className="text-[10px] text-gray-600 mt-1">{method.desc}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Summary */}
          <div className="lg:col-span-5">
            <div className="glass-card p-10 md:p-12 sticky top-32">
              <h2 className="text-2xl font-serif font-bold mb-10">Order Summary</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex justify-between items-center text-gray-500 text-sm">
                  <span>Subtotal</span>
                  <span className="text-white font-medium">₹{total().toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between items-center text-gray-500 text-sm">
                  <span>Shipping</span>
                  <span className="text-green-500 font-bold text-[10px] uppercase tracking-widest">Free</span>
                </div>
                <div className="border-t border-white/5 pt-6 flex justify-between items-center">
                  <span className="text-white font-bold text-lg">Total Amount</span>
                  <span className="text-3xl text-gold font-serif font-bold">₹{total().toLocaleString('en-IN')}</span>
                </div>
              </div>

              <button 
                onClick={handlePayment}
                disabled={isProcessing}
                className={`btn-primary w-full py-6 group relative overflow-hidden ${isProcessing ? 'opacity-80 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-midnight border-t-transparent rounded-full animate-spin mr-3" />
                    Processing...
                  </div>
                ) : (
                  <>
                    Complete Purchase
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
                  </>
                )}
              </button>

              <div className="mt-10 p-6 bg-white/5 border border-white/10 rounded-2xl space-y-4">
                <div className="flex items-center text-[10px] text-gray-600 uppercase tracking-widest">
                  <ShieldCheck className="w-4 h-4 mr-3 text-gold" />
                  100% Secure Transaction
                </div>
                <div className="flex items-center text-[10px] text-gray-600 uppercase tracking-widest">
                  <Lock className="w-4 h-4 mr-3 text-gold" />
                  Encrypted Data Protection
                </div>
              </div>
              
              <p className="mt-8 text-center text-[10px] text-gray-700 italic">
                By completing your purchase, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
